import {
  AppBar,
  Toolbar,
  Box,
  Tab,
  Tabs,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Typography,
  Divider,
  useMediaQuery,
  useTheme,
  Button,
  Menu,
} from "@mui/material";
import { Menu as MenuIcon, ArrowDropDown as ArrowDropDownIcon } from "@mui/icons-material";
import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { SearchBar } from "./SearchBar";
import { UserProfile } from "./UserProfile";
import { InvitationsBadge } from "./InvitationsBadge";
import { en } from "../translations/en";
import { useAuthorization } from "../hooks/useAuthorization";
import { usePermissions } from "../hooks/usePermissions";
import { isOwnerRole } from "../utils/roles";
import { CatalogMenuList } from "./CatalogMenuList";
import { useActiveCatalog } from "../hooks/useActiveCatalog";
import { useCollections } from "../hooks/useCollections";
import artLegacyLogo from "../assets/art-legacy-logo.png";

export const NavigationBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [catalogMenuAnchor, setCatalogMenuAnchor] = useState<null | HTMLElement>(null);
  const { permissions } = usePermissions();
  const {
    availableCatalogs,
    activeCatalogAliId,
    switchToCatalogAliId,
  } = useActiveCatalog();
  const { selectCollection } = useCollections();
  useAuthorization();

  const isViewer = permissions?.role === 'viewer';
  // Delegates are intentionally NOT shown the Bookmarks tab or the
  // InvitationsBadge below: those are owner-scoped (a delegate acts on someone
  // else's catalog and doesn't manage their own delegations). Catalog
  // selection, however, IS unified — delegates use the same Catalog tab +
  // dropdown as everyone else (the standalone delegate dashboard was removed).
  const isDelegate = permissions?.role === 'delegate';
  // Owner-role check shared with DefaultRedirect via ../utils/roles so the two
  // can't drift. Matches PermissionsContext's normalised hyphenated strings.
  const isCatalogOwner = isOwnerRole(permissions?.role);

  // Tab VISIBILITY is dual-gated: an owner always sees a path to their
  // catalog (even mid-upgrade with an empty list, where /my-objects shows an
  // empty-state guiding them to finish), and any non-owner with delegated
  // access also sees the tab. Dropdown chevron and active-catalog routing
  // remain data-driven below.
  const hasCatalogs = availableCatalogs.length > 0;
  const hasMultipleCatalogs = availableCatalogs.length > 1;
  const showCatalogTab = isCatalogOwner || hasCatalogs;

  // The "primary" target for clicking the Catalog tab label: the owned
  // primary if the user has one, else the first delegated catalog (pure-
  // delegate fallback so they don't land on an empty "My Catalog" view).
  const primaryAli =
    availableCatalogs.find((c) => c.kind === 'own-primary')?.catalogAliId ??
    availableCatalogs[0]?.catalogAliId ??
    null;

  // Build the visible tab list in render order. Each entry knows its route
  // (for index-based highlighting via Tabs.value) AND the action to fire on
  // click. The Catalog tab uses a sentinel "catalog" action so it routes
  // through handleCatalogTabClick instead of a plain navigate — that's where
  // the primary-catalog-switch + collection-clear lives.
  //
  // Keeping the list in one place means the Tabs JSX below and the click
  // dispatcher never drift apart: an empty `availableCatalogs` array
  // automatically removes the Catalog tab from BOTH visibility and the index
  // mapping in lockstep.
  type TabSpec = { route: string; action: 'navigate' | 'catalog' };
  const visibleTabs: TabSpec[] = [];
  if (showCatalogTab) visibleTabs.push({ route: '/my-objects', action: 'catalog' });
  visibleTabs.push({ route: '/search', action: 'navigate' });
  if (!isDelegate) visibleTabs.push({ route: '/bookmarks', action: 'navigate' });

  const getTabValue = (): number | false => {
    const idx = visibleTabs.findIndex((t) => t.route === location.pathname);
    return idx === -1 ? false : idx;
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const spec = visibleTabs[newValue];
    if (!spec) return;
    if (spec.action === 'catalog') {
      handleCatalogTabClick();
    } else {
      // No-op if already on this route — prevents oscillation when clicking
      // Search while already on /search?q=... (GH#1212).
      if (location.pathname === spec.route) return;
      navigate(spec.route);
    }
  };

  // Catalog tab label click — switch to primary catalog (or first delegated
  // for pure delegates), clear any active collection filter (regression
  // #1045), and navigate to /my-objects. The catalog-switch is guarded by an
  // idempotency check so we don't thrash dependent contexts when the user is
  // already on the primary; the collection clear and navigate always fire.
  const handleCatalogTabClick = () => {
    if (primaryAli && primaryAli !== activeCatalogAliId) {
      switchToCatalogAliId(primaryAli);
    }
    selectCollection(null);
    navigate("/my-objects");
  };

  const handleCatalogMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setCatalogMenuAnchor(event.currentTarget);
  };

  const handleCatalogMenuClose = () => {
    setCatalogMenuAnchor(null);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  if (isMobile) {
    return (
      <>
        <AppBar position="static" color="default" elevation={1}>
          <Toolbar
            sx={{
              justifyContent: "space-between",
              minHeight: "64px !important",
              px: 2,
            }}
          >
            {/* Logo */}
            <Box
              component={Link}
              to={isViewer && !hasCatalogs ? "/home" : "/my-objects"}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Box
                component="img"
                src={artLegacyLogo}
                alt={en.navigation.logoAlt}
                sx={{ height: 30, width: "auto", cursor: "pointer" }}
              />
            </Box>

            {/* Right side - Invitations, User Profile, and Mobile Menu */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {!isViewer && !isDelegate && <InvitationsBadge />}
              <UserProfile />
              <IconButton onClick={handleMobileMenuToggle} aria-label="Open menu">
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Mobile Drawer */}
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={handleMobileMenuToggle}
        >
          <Box sx={{ width: 250, pt: 2 }}>
            <List>
              <ListItem
                onClick={() => {
                  setMobileMenuOpen(false);
                  // No-op navigation when already on /search — prevents URL
                  // oscillation with active query params (GH#1212).
                  if (location.pathname !== '/search') {
                    navigate('/search');
                  }
                }}
                sx={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}
              >
                <ListItemText primary={en.navigation.searchTab} />
              </ListItem>
              {!isDelegate && (
                <ListItem
                  onClick={() => {
                    setMobileMenuOpen(false);
                    if (location.pathname !== '/bookmarks') {
                      navigate('/bookmarks');
                    }
                  }}
                  sx={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}
                >
                  <ListItemText primary={en.navigation.bookmarks} />
                </ListItem>
              )}
              {showCatalogTab && (
                <ListItem
                  onClick={() => {
                    handleCatalogTabClick();
                    setMobileMenuOpen(false);
                  }}
                  sx={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}
                >
                  <ListItemText primary={en.navigation.myObjects} />
                </ListItem>
              )}
            </List>
            {hasMultipleCatalogs && (
              <>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ px: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {en.catalogSwitcher.catalog}
                  </Typography>
                </Box>
                <CatalogMenuList onSelect={() => setMobileMenuOpen(false)} />
              </>
            )}
            <Box sx={{ p: 2 }}>
              <SearchBar />
            </Box>
          </Box>
        </Drawer>
      </>
    );
  }

  return (
    <AppBar position="static" color="default" elevation={1}>
      <Toolbar sx={{ justifyContent: "space-between", px: 2 }}>
        {/* Left side - Logo and Navigation */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            component={Link}
            to={isViewer && !hasCatalogs ? "/home" : "/my-objects"}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Box
              component="img"
              src={artLegacyLogo}
              alt={en.navigation.logoAlt}
              sx={{ height: 60, width: "auto", cursor: "pointer" }}
            />
          </Box>
          <Tabs
            value={getTabValue()}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                color: theme.palette.text.primary,
                minHeight: 48,
                textTransform: 'none',
                fontWeight: 500,
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                  fontWeight: 600
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.primary.main,
                height: 3
              }
            }}
          >
            {/* All desktop tabs navigate solely through the Tabs onChange handler
              * (handleTabChange). Using component={Link} would cause double-navigation
              * AND would bypass the same-route guard that prevents the GH#1212 URL
              * oscillation on Search (and, symmetrically, on Bookmarks). */}
            {showCatalogTab && (
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Button
                      data-testid="catalog-tab-label"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCatalogTabClick();
                      }}
                      sx={{
                        color: 'inherit',
                        textTransform: 'none',
                        fontWeight: 'inherit',
                        fontSize: 'inherit',
                        p: 0,
                        minWidth: 0,
                        '&:hover': { backgroundColor: 'transparent' },
                      }}
                      disableRipple
                    >
                      {en.navigation.myObjects}
                    </Button>
                    {hasMultipleCatalogs && (
                      <IconButton
                        data-testid="catalog-tab-dropdown-button"
                        size="small"
                        onClick={handleCatalogMenuOpen}
                        sx={{ p: 0, ml: 0.25 }}
                        disableRipple
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    )}
                  </Box>
                }
                sx={{ p: 0 }}
              />
            )}
            <Tab
              label={en.navigation.searchTab}
            />
            {!isDelegate && (
              <Tab
                label={en.navigation.bookmarks}
              />
            )}
          </Tabs>

          {/* Unified catalog dropdown — owned + delegated rows in one menu. */}
          <Menu
            anchorEl={catalogMenuAnchor}
            open={Boolean(catalogMenuAnchor)}
            onClose={handleCatalogMenuClose}
          >
            <CatalogMenuList onSelect={handleCatalogMenuClose} />
          </Menu>
        </Box>

        {/* Center - Search Bar */}
        <Box sx={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', mx: 2 }}>
          <SearchBar />
        </Box>

        {/* Right side - Invitations and User Profile */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {!isViewer && !isDelegate && <InvitationsBadge />}
          <UserProfile />
        </Box>
      </Toolbar>
    </AppBar>
  );
};
